import { useCallback, useEffect, useMemo, useState } from 'react';
import { USE_MOCKS, apiGet, apiPatch, apiPost, apiDelete } from '../api/client';
import { useBrand } from '../brand/BrandProvider';
import {
  Check,
  Laptop,
  Smartphone,
  Monitor,
  Tablet,
  ShieldCheck,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/Button';
import { Card, CardHeader } from '../components/Card';
import { Badge } from '../components/Badge';
import { Input } from '../components/Input';
import { Tabs } from '../components/Tabs';
import { Modal } from '../components/Modal';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import {
  getTwoFactorStatus, startTwoFactorEnable, confirmTwoFactorEnable,
  startTwoFactorDisable, confirmTwoFactorDisable, type TwoFactorStatus,
} from '../api/auth';
import { cn } from '../lib/cn';
import type { DeviceSession } from '../mocks/clientBatch';
import { useQueryClient } from '@tanstack/react-query';
import {
  useMyActiveSessions,
  revokeMySession,
  revokeAllMySessions,
  useMyProfile,
  updateMyProfile,
  SESSION_CAP_DEFAULT,
} from '../api/platform';
import { useSession } from '../stores/session';

export function Profile() {
  const { data: myActiveSessions } = useMyActiveSessions();
  // Session state lifted out so we can show a live count in the tab badge
  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const activeSessionCount = sessions.filter((s) => !s.revokedAt).length;

  // Seed the editable session list once the fetched sessions land.
  useEffect(() => {
    if (!myActiveSessions) return;
    setSessions(myActiveSessions.map((s) => ({ ...s })));
  }, [myActiveSessions]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Account"
        title="Profile"
        subtitle="Personal information, security, sessions, and notifications."
      />

      <Card className="px-5 sm:px-6 pt-2 pb-5 sm:pb-6">
        <Tabs
          tabs={[
            { id: 'personal', label: 'Personal', content: <PersonalTab /> },
            { id: 'security', label: 'Security', content: <SecurityTab /> },
            {
              id: 'sessions',
              label: 'Sessions',
              content: <SessionsTab sessions={sessions} setSessions={setSessions} />,
              count: activeSessionCount,
            },
            { id: 'notifications', label: 'Notifications', content: <NotificationsTab /> },
          ]}
        />
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function PersonalTab() {
  // Load the persisted profile so saved fields (name, mobile) survive a refresh —
  // the session identity only carries id/name/email, never contactPhone.
  const { data: profile, isLoading } = useMyProfile();
  const sessionUser = useSession((s) => s.user);
  const setSessionUser = useSession((s) => s.setUser);
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form once the real profile lands (and re-seed if it changes).
  useEffect(() => {
    if (!profile) return;
    setName(profile.name ?? '');
    setPhone(profile.contactPhone ?? '');
  }, [profile]);

  async function save() {
    if (!name.trim() || busy) return;
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      const updated = await updateMyProfile({ name: name.trim(), contactPhone: phone.trim() || null });
      // Reflect the persisted values everywhere: the profile cache (so a refresh /
      // tab-switch reads them back) and the session name (topbar + dashboard greeting).
      qc.setQueryData(['me', 'profile'], updated);
      if (sessionUser) setSessionUser({ ...sessionUser, name: updated.name });
      setName(updated.name ?? '');
      setPhone(updated.contactPhone ?? '');
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your changes. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  function discard() {
    setName(profile?.name ?? '');
    setPhone(profile?.contactPhone ?? '');
    setError(null);
  }

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-5">
        <Input
          label="Full name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isLoading}
        />
        <Input
          label="Email"
          type="email"
          value={profile?.email ?? ''}
          readOnly
          disabled
          hint="Your sign-in email. Contact support to change it."
        />
        <Input
          label="Mobile (BD)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+8801XXXXXXXXX"
          disabled={isLoading}
          hint="Receives SMS OTPs and dunning alerts."
        />
      </div>
      <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3 flex-wrap">
        {error && <span className="text-sm text-rose-600 dark:text-rose-400 mr-auto">{error}</span>}
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button variant="ghost" onClick={discard} disabled={busy || isLoading}>Discard</Button>
        <Button onClick={save} loading={busy} disabled={busy || isLoading || !name.trim()}>Save changes</Button>
      </div>
    </div>
  );
}

/* ── Security tab ──────────────────────────────────────────────────── */

function SecurityTab() {
  const [pwOpen, setPwOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const signOut = useSession((s) => s.signOut);

  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-6">
        <CardHeader
          title="Password"
          subtitle="Keep your account secure with a strong password."
          action={<Button variant="secondary" size="sm" onClick={() => setPwOpen(true)}>Change password</Button>}
        />
      </Card>
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />

      <TwoFactorCard />

      <Card className="p-5 sm:p-6">
        <CardHeader title="Account" />
        <div className="rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-rose-900 dark:text-rose-200">
              Delete this user account
            </p>
            <p className="text-sm text-rose-800 dark:text-rose-300 mt-0.5">
              Closes your account and signs you out everywhere. You must transfer or close any business you own first.
            </p>
          </div>
          <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>Delete</Button>
        </div>
      </Card>

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => { signOut(); window.location.href = '/login'; }}
      />
    </div>
  );
}


/* ── Two-factor login (#24) ──────────────────────────────────────── */

/**
 * Turn two-factor login on or off. Off by default, available to every role, and
 * the user picks email or SMS.
 *
 * Both directions are two-step and both require a working code:
 *   - enabling, because a factor you cannot actually receive is a lockout, and
 *     the only way to know it works is to make you use it once;
 *   - disabling, because the point of a second factor is that a stolen password
 *     is not enough — accepting one to remove it would hand that straight back.
 *
 * SMS is rendered but disabled while the gateway is unconfigured, with the
 * server's own reason shown, rather than hidden: "why can I not pick SMS?" is a
 * support ticket, and the answer costs one line.
 */
function TwoFactorCard() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [step, setStep] = useState<'idle' | 'enabling' | 'disabling'>('idle');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await getTwoFactorStatus();
      setStatus(s);
      if (s.channel) setChannel(s.channel);
    } catch {
      /* the card simply stays in its loading state */
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  function reset() {
    setStep('idle');
    setCode('');
    setSentTo(null);
    setError(null);
  }

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (!status) {
    return (
      <Card className="p-5 sm:p-6">
        <div className="skeleton h-5 w-48 rounded" />
      </Card>
    );
  }

  return (
    <Card className="p-5 sm:p-6">
      <CardHeader
        title="Two-factor login"
        subtitle={
          status.enabled
            ? `On — we send a code ${status.channel === 'sms' ? 'by SMS' : 'by email'}${status.destination ? ` to ${status.destination}` : ''} every time you sign in.`
            : 'Off. Add a second step at sign-in so a stolen password is not enough on its own.'
        }
        action={
          step === 'idle' ? (
            status.enabled ? (
              <Button
                variant="secondary"
                size="sm"
                loading={busy}
                onClick={() => void run(async () => {
                  const r = await startTwoFactorDisable();
                  if (r.removed) {
                    setNotice('Two-factor login has been switched off.');
                    await refresh();
                    return;
                  }
                  setSentTo(r.destination ?? null);
                  setStep('disabling');
                })}
              >
                Turn off
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep('enabling')}>Turn on</Button>
            )
          ) : (
            <Button variant="ghost" size="sm" onClick={reset} disabled={busy}>Cancel</Button>
          )
        }
      />

      {notice && step === 'idle' && (
        <Banner tone="success" className="mt-4" onDismiss={() => setNotice(null)}>{notice}</Banner>
      )}

      {step === 'enabling' && (
        <div className="mt-4 space-y-4">
          {!sentTo ? (
            <>
              <div>
                <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                  Where should we send your code?
                </span>
                <div className="space-y-2">
                  <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
                    <input type="radio" name="2fa-channel" checked={channel === 'email'} onChange={() => setChannel('email')} className="text-brand focus:ring-brand" />
                    Email
                  </label>
                  <label
                    className={
                      'flex items-center gap-2.5 text-sm ' +
                      (status.smsAvailable ? 'text-zinc-700 dark:text-zinc-200 cursor-pointer' : 'text-zinc-400 dark:text-zinc-500 cursor-not-allowed')
                    }
                  >
                    <input
                      type="radio"
                      name="2fa-channel"
                      checked={channel === 'sms'}
                      disabled={!status.smsAvailable}
                      onChange={() => setChannel('sms')}
                      className="text-brand focus:ring-brand"
                    />
                    SMS
                    {!status.smsAvailable && <span className="text-xs">— {status.smsUnavailableReason}</span>}
                  </label>
                </div>
              </div>
              {channel === 'sms' && !status.hasPhone && (
                <Banner tone="warning">Add a mobile number on the Personal tab first.</Banner>
              )}
              <Button
                size="sm"
                loading={busy}
                disabled={busy || (channel === 'sms' && (!status.smsAvailable || !status.hasPhone))}
                onClick={() => void run(async () => {
                  const r = await startTwoFactorEnable(channel);
                  setSentTo(r.destination);
                })}
              >
                Send code
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                We sent a code to <strong>{sentTo}</strong>. Enter it to finish switching two-factor on.
              </p>
              <Input label="Verification code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))} />
              <Button
                size="sm"
                loading={busy}
                disabled={busy || code.trim().length < 4}
                onClick={() => void run(async () => {
                  await confirmTwoFactorEnable(channel, code.trim());
                  setNotice('Two-factor login is on. You will need a code next time you sign in.');
                  reset();
                  await refresh();
                })}
              >
                Turn on two-factor
              </Button>
            </>
          )}
          {error && <Banner tone="error">{error}</Banner>}
        </div>
      )}

      {step === 'disabling' && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">
            We sent a code to <strong>{sentTo}</strong>. Enter it to switch two-factor off — we ask for
            a code rather than your password, because a password on its own is exactly what this
            protects against.
          </p>
          <Input label="Verification code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))} />
          <Button
            variant="destructive"
            size="sm"
            loading={busy}
            disabled={busy || code.trim().length < 4}
            onClick={() => void run(async () => {
              await confirmTwoFactorDisable(code.trim());
              setNotice('Two-factor login has been switched off.');
              reset();
              await refresh();
            })}
          >
            Turn off two-factor
          </Button>
          {error && <Banner tone="error">{error}</Banner>}
        </div>
      )}
    </Card>
  );
}

/* ── Change password ─────────────────────────────────────────────── */

function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function reset() {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    setDone(false);
  }
  function close() {
    reset();
    onClose();
  }

  async function submit() {
    setError(null);
    if (next.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      if (!USE_MOCKS) await apiPost('/me/password', { currentPassword: current, newPassword: next });
      setDone(true);
      setTimeout(close, 1200);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 401 ? 'Current password is incorrect.' : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Change password"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} loading={busy} disabled={busy || !current || !next || !confirm}>
            Update password
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        {done && <div className="text-sm text-emerald-700 dark:text-emerald-400">Password updated ✓</div>}
        <Input label="Current password" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="••••••••" />
        <Input label="New password" type="password" required value={next} onChange={(e) => setNext(e.target.value)} hint="At least 8 characters" placeholder="••••••••" />
        <Input label="Confirm new password" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
      </div>
    </Modal>
  );
}

/* ── Delete account ──────────────────────────────────────────────── */

function DeleteAccountModal({ open, onClose, onDeleted }: { open: boolean; onClose: () => void; onDeleted: () => void }) {
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = confirm.trim().toUpperCase() === 'DELETE';

  function close() {
    setConfirm('');
    setError(null);
    onClose();
  }

  async function submit() {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      if (!USE_MOCKS) await apiDelete('/me');
      onDeleted();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 409 ? 'You still own an active business. Transfer or close it before deleting your account.' : 'Could not delete the account.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Delete account"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={submit} loading={busy} disabled={!valid || busy}>Delete my account</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
        )}
        <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 p-3 text-sm text-rose-900 dark:text-rose-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>This closes your account and signs you out everywhere. You must transfer or close any business you own first.</span>
        </div>
        <Input label="Type DELETE to confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE" />
      </div>
    </Modal>
  );
}

/* ── Sessions tab ───────────────────────────────────────────────── */

function SessionIcon({ kind }: { kind: DeviceSession['deviceIcon'] }) {
  const cls = 'w-4 h-4';
  if (kind === 'laptop') return <Laptop className={cls} />;
  if (kind === 'monitor') return <Monitor className={cls} />;
  if (kind === 'smartphone') return <Smartphone className={cls} />;
  return <Tablet className={cls} />;
}

function SessionsTab({
  sessions,
  setSessions,
}: {
  sessions: DeviceSession[];
  setSessions: React.Dispatch<React.SetStateAction<DeviceSession[]>>;
}) {
  const brand = useBrand();
  const activeCount = useMemo(() => sessions.filter((s) => !s.revokedAt).length, [sessions]);
  const [confirmRevoke, setConfirmRevoke] = useState<DeviceSession | null>(null);
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);
  const [busy, setBusy] = useState(false);

  async function revokeSession(id: string) {
    setBusy(true);
    try {
      await revokeMySession(id);
      setSessions((prev) =>
        prev.map((s) =>
          s.id === id ? { ...s, revokedAt: new Date().toISOString(), revokedReason: 'user_signed_out' } : s,
        ),
      );
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not revoke the session.');
    } finally {
      setBusy(false);
      setConfirmRevoke(null);
    }
  }

  // The token doesn't identify which row is "this device", so the server revokes
  // EVERYTHING (sessions + refresh tokens); this signed-in tab lives only until
  // its short-lived access token expires.
  async function revokeAllOthers() {
    setBusy(true);
    try {
      await revokeAllMySessions();
      setSessions((prev) =>
        prev.map((s) => (s.revokedAt ? s : { ...s, revokedAt: new Date().toISOString(), revokedReason: 'user_signed_out' })),
      );
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not revoke the sessions.');
    } finally {
      setBusy(false);
      setConfirmRevokeAll(false);
    }
  }

  return (
    <Card className="overflow-hidden">
      <div className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 border-b border-zinc-200 dark:border-zinc-700">
        <div>
          <h2 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
            Active sessions ({activeCount} of {SESSION_CAP_DEFAULT} device cap)
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-0.5">
            Devices currently signed in to your {brand.name} account.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConfirmRevokeAll(true)}
          disabled={sessions.filter((s) => !s.isCurrent && !s.revokedAt).length === 0}
        >
          Revoke all others
        </Button>
      </div>

      <div className="overflow-x-auto nice-scroll">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
            <tr>
              <th className="text-left font-medium px-5 sm:px-6 py-3">Device</th>
              <th className="text-left font-medium px-4 py-3">Location</th>
              <th className="text-left font-medium px-4 py-3">Last active</th>
              <th className="text-right font-medium px-5 sm:px-6 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {sessions.map((s) => (
              <tr key={s.id} className={cn('transition', s.revokedAt ? 'opacity-60' : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/70')}>
                <td className="px-5 sm:px-6 py-3">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-md bg-zinc-100 dark:bg-zinc-800 grid place-items-center text-zinc-500 dark:text-zinc-400">
                      <SessionIcon kind={s.deviceIcon} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{s.deviceLabel}</div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{s.userAgent.length > 50 ? s.userAgent.slice(0, 50) + '…' : s.userAgent}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 tabular-nums">
                  {s.geoCity}, {s.geoCountry}
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">{s.lastActiveAt}</td>
                <td className="px-5 sm:px-6 py-3 text-right">
                  {s.revokedAt ? (
                    <span className="text-xs text-zinc-400 dark:text-zinc-500">Revoked</span>
                  ) : s.isCurrent ? (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-emerald-700 dark:text-emerald-400">
                      <span aria-hidden>★</span> Current
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmRevoke(s)}>
                      Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 sm:px-6 py-4 bg-zinc-50/50 dark:bg-zinc-900/50 border-t border-zinc-200 dark:border-zinc-700">
        <p className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" />
          Revoking a session immediately invalidates that device's refresh token.
        </p>
      </div>

      <Modal
        open={confirmRevoke !== null}
        onClose={() => !busy && setConfirmRevoke(null)}
        title="Revoke session"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRevoke(null)} disabled={busy}>Cancel</Button>
            <Button
              variant="destructive"
              loading={busy}
              onClick={() => confirmRevoke && revokeSession(confirmRevoke.id)}
            >
              Sign out from {confirmRevoke?.deviceLabel}
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          Sign out from <strong>{confirmRevoke?.deviceLabel}</strong>? This will end the session immediately and the device will need to log in again.
        </p>
      </Modal>

      <Modal
        open={confirmRevokeAll}
        onClose={() => !busy && setConfirmRevokeAll(false)}
        title="Revoke all other sessions"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRevokeAll(false)} disabled={busy}>Cancel</Button>
            <Button variant="destructive" loading={busy} onClick={revokeAllOthers}>Revoke all others</Button>
          </>
        }
      >
        <p className="text-sm text-zinc-700 dark:text-zinc-200">
          You'll stay signed in on this device. All other active sessions will be ended.
        </p>
      </Modal>
    </Card>
  );
}

/* ── Notifications preferences (unchanged feature) ─────────────────── */

/** Canonical opt-in list — stable keys persist to users.notification_prefs. */
const NOTIF_GROUPS: Array<{ title: string; items: Array<{ key: string; label: string; defaultOn: boolean }> }> = [
  {
    title: 'Billing',
    items: [
      { key: 'payment_receipts', label: 'Payment receipts', defaultOn: true },
      { key: 'payment_failures', label: 'Payment failures', defaultOn: true },
      { key: 'dunning_reminders', label: 'Dunning reminders (when overdue)', defaultOn: true },
      { key: 'trial_ending', label: 'Trial ending in 3 days', defaultOn: true },
      { key: 'plan_changes', label: 'Plan changes I make', defaultOn: true },
      { key: 'price_change_notice', label: 'Price-change advance notice (60 days before)', defaultOn: false },
    ],
  },
  {
    title: 'Account & access',
    items: [
      { key: 'new_device_signin', label: 'New device sign-in', defaultOn: true },
      { key: 'credential_changes', label: 'Password / email / 2FA changes', defaultOn: true },
      { key: 'team_changes', label: 'Team member added or removed', defaultOn: true },
    ],
  },
  {
    title: 'Operational',
    items: [
      { key: 'daily_pos_summary', label: 'Daily POS summary', defaultOn: false },
      { key: 'weekly_revenue_report', label: 'Weekly revenue report', defaultOn: false },
      { key: 'backup_completed', label: 'Database backup-completed confirmation', defaultOn: false },
    ],
  },
];

const NOTIF_DEFAULTS: Record<string, boolean> = Object.fromEntries(
  NOTIF_GROUPS.flatMap((g) => g.items.map((it) => [it.key, it.defaultOn])),
);

function NotificationsTab() {
  const [prefs, setPrefs] = useState<Record<string, boolean>>(NOTIF_DEFAULTS);
  const [loaded, setLoaded] = useState<Record<string, boolean>>(NOTIF_DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  // A failed FETCH used to be swallowed, leaving the hardcoded defaults on screen as if
  // they were the user's saved settings — and, because nothing was dirty, Save stayed
  // disabled, so they could not even correct it. Say the load failed instead.
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dirty = useMemo(() => NOTIF_GROUPS.some((g) => g.items.some((it) => prefs[it.key] !== loaded[it.key])), [prefs, loaded]);

  useEffect(() => {
    if (USE_MOCKS) return;
    let alive = true;
    apiGet<Record<string, boolean>>('/me/notification-preferences')
      .then((server) => {
        if (!alive) return;
        const merged = { ...NOTIF_DEFAULTS, ...server };
        setPrefs(merged);
        setLoaded(merged);
        setLoadError(false);
      })
      .catch(() => {
        if (alive) setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function save() {
    if (busy) return;
    setBusy(true);
    setSaved(false);
    setSaveError(null);
    try {
      if (!USE_MOCKS) await apiPatch('/me/notification-preferences', { prefs });
      setLoaded(prefs);
      setSaved(true);
      setTimeout(() => setSaved(false), 2400);
    } catch (e) {
      // Same rule as the fetch: a failed save must never look like a successful one.
      setSaveError(e instanceof Error ? e.message : 'Could not save your notification preferences.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      {loadError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200">
          We couldn’t load your saved notification settings, so the switches below show the
          defaults rather than your choices. Reload the page to try again — saving now would
          overwrite what you have set.
        </div>
      )}
      {NOTIF_GROUPS.map((g) => (
        <NotifGroup
          key={g.title}
          title={g.title}
          items={g.items.map((it) => ({ key: it.key, label: it.label, value: prefs[it.key] ?? it.defaultOn }))}
          onToggle={(key, value) => setPrefs((p) => ({ ...p, [key]: value }))}
        />
      ))}

      <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700 p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
          Required (cannot opt out)
        </div>
        <ul className="space-y-1.5 text-sm text-zinc-700 dark:text-zinc-200">
          <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-zinc-400" /> Account security alerts</li>
          <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-zinc-400" /> Legal / Terms updates</li>
          <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-zinc-400" /> Refund / chargeback notices</li>
        </ul>
      </div>

      <div className="pt-4 border-t border-zinc-200 dark:border-zinc-700 flex justify-end items-center gap-3">
        {saveError && <span className="text-sm text-rose-600 dark:text-rose-400">{saveError}</span>}
        {saved && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
        <Button variant="ghost" onClick={() => setPrefs(loaded)} disabled={busy || !dirty}>Discard</Button>
        <Button onClick={save} loading={busy} disabled={busy || !dirty}>Save changes</Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function DeviceRow({
  icon,
  name,
  loc,
  lastSeen,
  trusted = false,
}: {
  icon: React.ReactNode;
  name: string;
  loc: string;
  lastSeen: string;
  trusted?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-md bg-zinc-100 dark:bg-zinc-800 grid place-items-center text-zinc-500 dark:text-zinc-400">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{name}</div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums">
          {loc} · last seen {lastSeen}
        </div>
      </div>
      {trusted && <Badge tone="success">Trusted</Badge>}
    </div>
  );
}

function NotifGroup({
  title,
  items,
  onToggle,
}: {
  title: string;
  items: Array<{ key: string; label: string; value: boolean }>;
  onToggle: (key: string, value: boolean) => void;
}) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
        {title}
      </div>
      <Card className="divide-y divide-zinc-200 dark:divide-zinc-700">
        {items.map((it) => (
          <ToggleRow key={it.key} label={it.label} value={it.value} onChange={(v) => onToggle(it.key, v)} />
        ))}
      </Card>
    </div>
  );
}

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-4 px-5 sm:px-6 py-3 cursor-pointer">
      <span className="text-sm text-zinc-800 dark:text-zinc-100">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={cn(
          'relative w-9 h-5 rounded-full transition-colors',
          value ? 'bg-brand dark:bg-brand-dm' : 'bg-zinc-200 dark:bg-zinc-700',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform',
            value ? 'translate-x-[18px]' : 'translate-x-0.5',
          )}
        />
      </button>
    </label>
  );
}
